import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getById } from "@insite/client-framework/Store/Data/DataState";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";


export const cartProductIsPurchasableOnWeb = (cartLine: CartLineModel) => {
    if (cartLine.properties.nonPurchasableOnWeb === undefined) {
        return true;
    }
    return (
        cartLine.properties.nonPurchasableOnWeb === undefined ||
        cartLine.properties.nonPurchasableOnWeb.toLowerCase() === "false"
    );
};

export const getIsPurchasableOnWebOverall = (cart: Readonly<Cart> | undefined) => {
    let result = true;
    if (cart === undefined || cart.cartLines === undefined || cart.cartLines === null || cart.cartLines?.length === 0) {
        result = true;
    } else {
        cart.cartLines.forEach(cartLine => {
            if (cartLine.properties !== undefined) {
                if (cartLine.properties.nonPurchasableOnWeb !== undefined) {
                    if (cartLine.properties.nonPurchasableOnWeb.toLowerCase() === "true") {
                        result = false;
                    }
                }
            }
        });
    }

    return result;
};

export const getFreeFreightData = (cart: Readonly<Cart> | undefined, state: ApplicationState) => {
    let nonBulkItemSum = 0;
    let bulkOrCaseItemsSum = 0;
    let cartLines = null;

    if (cart) {
        cartLines = cart.cartLines;
    }

    if (cartLines) {
        for (let i = 0; i < cartLines.length; i++) {
            const product = cartLines[i];
            if (product) {
                const attributeValues = product.properties.pmFreightClass;
                if (attributeValues) {
                    if (attributeValues.includes("Normal Item")) {
                        nonBulkItemSum += cartLines[i].pricing!.unitNetPrice * cartLines[i].qtyOrdered!;
                    }

                    if (attributeValues.includes("Bulk/Heavy Item") || attributeValues.includes("Per Case item")) {
                        bulkOrCaseItemsSum += cartLines[i].pricing!.unitNetPrice * cartLines[i].qtyOrdered!;
                    }
                } else {
                    nonBulkItemSum += cartLines[i].pricing!.unitNetPrice * cartLines[i].qtyOrdered!;
                }
            }
        }
    }
    return {
        nonBulkItemSum,
        bulkOrCaseItemsSum,
    };
};

export const getIsDiscontinuedCart = (cartLine: CartLineModel) => {
    if (cartLine.properties.discontinuedStatus === undefined) {
        return false;
    }
    return cartLine.properties.discontinuedStatus.toLowerCase() === "discontinued";
};

export const getIsTempNotForSaleCart = (cartLine: CartLineModel) => {
    if (cartLine.properties.discontinuedStatus === undefined) {
        return false;
    }
    return cartLine.properties.discontinuedStatus.toLowerCase() === "temp not for sale";
};

export const getToBeDiscontinuedCart = (cartLine: CartLineModel) => {
    if (cartLine.properties.discontinuedStatus === undefined) {
        return false;
    }
    return cartLine.properties.discontinuedStatus.toLowerCase() === "to be discontinued";
};

export const getPartNumberChangeCart = (cartLine: CartLineModel) => {
    if (cartLine.properties.discontinuedStatus === undefined) {
        return false;
    }
    return cartLine.properties.discontinuedStatus.toLowerCase() === "part number change";
};

export const getIsDiscontinuedCartOverall = (cart: Readonly<Cart> | undefined) => {
    let isDiscontinuedOverall = false;
    if (cart === undefined || cart.cartLines === undefined || cart.cartLines === null || cart.cartLines?.length === 0) {
        isDiscontinuedOverall = false;
    } else {
        cart.cartLines.forEach(cartLine => {
            if (cartLine.properties !== undefined) {
                if (cartLine.properties.discontinuedStatus !== undefined) {
                    if (
                        cartLine.properties.discontinuedStatus.toLowerCase() === "discontinued"
                    ) {
                        isDiscontinuedOverall = true;
                    }
                }
            }
        });
    }
    return isDiscontinuedOverall;
};

export const getIsTempNotForSaleCartOverall = (cart: Readonly<Cart> | undefined) => {
    let isTempNotForSaleOverall = false;

    if (cart === undefined || cart.cartLines === undefined || cart.cartLines === null || cart.cartLines.length === 0) {
        isTempNotForSaleOverall = false;
    } else {
        cart.cartLines.forEach(cartLine => {
            if (cartLine.properties !== undefined) {
                if (cartLine.properties.discontinuedStatus !== undefined) {
                    if (cartLine.properties.discontinuedStatus.toLowerCase() === "temp not for sale") {
                        isTempNotForSaleOverall = true;
                    }
                }
            }
        });
    }

    return isTempNotForSaleOverall;
};

export const getToBeDiscontinuedAndOutOfStockCartOverall = (cart: Readonly<Cart> | undefined) => {
    let toBeDiscontinuedAndOutOfStock = false;

    if (!cart?.cartLines?.length) {
        return false;
    }

    cart.cartLines.forEach(cartLine => {
        if (getToBeDiscontinuedCart(cartLine)) {
            if (!cartLineHasInventory(cartLine)) {
                toBeDiscontinuedAndOutOfStock = true;
            }
        }
    });

    return toBeDiscontinuedAndOutOfStock;
};

export const getPartNumberChangeAndOutOfStockCartOverall = (cart: Readonly<Cart> | undefined) => {
    let partNumberChangeAndOutOfStock = false;

    if (!cart?.cartLines?.length) {
        return false;
    }

    cart.cartLines.forEach(cartLine => {
        if (getPartNumberChangeCart(cartLine)) {
            if (!cartLineHasInventory(cartLine)) {
                partNumberChangeAndOutOfStock = true;
            }
        }
    });

    return partNumberChangeAndOutOfStock;
};

export const cartLineIsDropShipItem = (cartLine: CartLineModel): boolean => {
    const dropShipAttribute = cartLine.properties?.itemDropShip;
    return dropShipAttribute ? dropShipAttribute.toLowerCase() === "yes" : false;
};

export const hasHazmatItems = (cart: Readonly<Cart> | undefined): boolean => {
    return cart?.cartLines?.some(line => line.properties?.itemHazmat?.toLowerCase() === "yes") ?? false;
};

export const isAirFreightSelected = (cart: Readonly<Cart> | undefined): boolean => {
    if (!cart || !cart.shipVia || !cart.carriers) {
        return false;
    }

    for (const carrier of cart.carriers) {
        const shipVias = carrier.shipVias ?? [];
        const matchingShipVia = shipVias.find(shipVia => shipVia.id === cart.shipVia?.id);

        if (matchingShipVia) {
            const isAirFreight = (matchingShipVia as any).isAirFreight as boolean;
            if (isAirFreight) {
                return true;
            }
        }
    }

    return false;
};

export const cartLineHasInventory = (cartLine: CartLineModel): boolean => {
    if(cartLine.availability?.messageType === 2){
        return false;
    }

    return true;
};

export const getRxItemLinesCartOverall = (cart: Readonly<Cart> | undefined) => {
    if (!cart?.cartLines?.length) {
        return false;
    }

    return cart.cartLines.some(cartLine =>
        cartLine.properties?.itemType &&
        cartLine.properties.itemType.toLowerCase() !== "standard"
    );
};

export const hasRxItems = (cart: Readonly<Cart> | undefined): boolean => {
    return !!cart?.cartLines?.some(
        line => line.properties?.itemType && line.properties.itemType.toLowerCase() !== "standard"
    );
};
